import fetcher, {
	FleetFile,
	FleetPaginated,
	FleetQueryParams,
} from '../fetcher';
import {
	FilePayload,
	FileData,
	CustomerLegalDocumentPayload,
	CustomerBusinessDocumentPayload,
	CustomerReferralDocumentPayload,
	CustomerManagementInfoPayload,
	CustomerShareholderPayload,
	CustomerRejectionHistory,
} from './customer';
import { MasterData } from './master-data';
import {
	ApprovalHistory,
	CustomerBusinessDocument,
	CustomerLegalDocument,
	ExistingFinancingLimitInformation,
	FinancingApplicationLimitInformation,
	MainInformation,
	SurveyParticipant,
} from './types';

export type ProspectPlafon = {
	segmentMarketId: number;
	orderTypeId: number;
	orderSourceId: number;
	plafonTypeId: number;
	applicationLimit: number;
	referrerName: string;
	referrerPhoneNumber: string;
	referrerPositionId: number;
	referrerUnitId: number;
	supplierDealer?: string;
	financingTypeIds: number[];
	financingTypeDetailIds: number[];
	financingObjectIds: number[];
	companyPlafonId?: number;
	financingLimitTypeId?: number;
};

export type ProspectPayload = { customerId: string; plafons: ProspectPlafon[] };

export type Referrer = {
	unitId: number;
	unitName: string;
	name: string;
	phoneNumber: string;
	position: MasterData;
};

export type ProspectPlafonDetail = {
	prospectId: string;
	prospectPlafonId: number;
	orderType: MasterData;
	orderSource: MasterData;
	segmentMarket: MasterData;
	referrer: Referrer;
	limitApplication: number;
	plafonType: MasterData;
	financingTypeIds: number[];
	financingObjectIds: number[];
};

export const submitProspect = (body: ProspectPayload) => {
	return fetcher<string>({ api: '/prospect/submit', body });
};

export type RequestSLIKCheckPayload = {
	prospectId: string;
	slikFormFile?: FilePayload | FileData;
	applicationLetterFile?: FilePayload | FileData;
	additionalInformation: string;
	slikCheckingStatus?: number;
	slikCheckingDate?: string;
};

export type RequestSLIKCCAPayload = {
	prospectId: string;
	additionalInformation: string;
	isRecheckSlik?: boolean;
};

export const requestSLIKCheck = (body: RequestSLIKCheckPayload) => {
	return fetcher({ api: '/prospect/submit-customer-checking', body });
};

export const requestSLIKCCA = (body: RequestSLIKCCAPayload) => {
	return fetcher({ api: '/prospect/cca/request-slik', body });
};

export type ProspectList = {
	no: number;
	prospectId: string;
	customerId: string;
	customerName: string;
	customerGroupName: string;
	picName: string;
	prospectCreatedAt: string;
	department: string;
	frmName: string;
	sectorIndustryName: string;
	npwp?: string;
	status: string;
	reviewStatus: string;
	industryClass: string;
	pendingUser?: string;
};

export const getProspectList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-list',
		body: defaultParams,
	});
};

export const getBusinessCheckList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-business-check-list',
		body: defaultParams,
	});
};

export const getBusinessApprovalList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-business-approval-list',
		body: defaultParams,
	});
};

export const getAssignmentList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-assignment-list',
		body: defaultParams,
	});
};

export const getCreditAssignList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-credit-assign-list',
		body: defaultParams,
	});
};

export const getCreditCheckList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-credit-check-list',
		body: defaultParams,
	});
};

export const getCreditApprovalList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/prospect/get-credit-approval-list',
		body: defaultParams,
	});
};

export const getProspectPlafonDetail = (prospectPlafonId: number) => {
	return fetcher<ProspectPlafonDetail>({
		api: '/prospect-plafon/get',
		body: { prospectPlafonId },
	});
};

export type ProspectCustomerCheckingAppovalPayload = {
	prospectId: string;
	approvalStatus: string;
	additionalInformation: string;
};

export const submitProspectCustomerCheckingApproval = (
	body: ProspectCustomerCheckingAppovalPayload,
) => {
	return fetcher({ api: '/prospect/submit-customer-checking-approval', body });
};

export type SubmitCreditAnalystPayload = {
	prospectId: string;
	creditAnalystId: number;
	notes: string;
};

export const submitCreditAnalyst = (body: SubmitCreditAnalystPayload) => {
	return fetcher({ api: '/prospect/submit-credit-assignment', body });
};

export type customerNegativeHistory = {
	customerName: string;
	isActive: string;
	negativeDate: string;
	negativeId: number;
	negativeStatus: string;
	negativeType: string;
	no: number;
	prospectId: string;
	source: string;
	lastSktDate: string;
	skt: number;
	notes: string;
};

export type ShareholderSlikInformation = {
	id: number;
	idebFile?: FileData;
	slikResult?: number;
	name: string;
	npwp: string;
	identityNumber: string;
	plafon: null;
	outstandingDebit: null;
	principalArrears: null;
};

export type ProspectCustomerCreditCheck = {
	approvalHistory: {
		no: number;
		approvalBy: string;
		approvalDate: string;
		approvalNote: string;
		approvalStatus: string;
		approvalProcess: string;
	}[];
	creditExternalChecks: {
		category: 'dukcapil' | 'dttot' | 'dppspm' | 'pep';
		analysisResult: string;
		analysisBy: string;
		creditExternalQuestions: {
			evidenceFile: FileData;
			question: MasterData;
			answer: MasterData;
			message?: string;
		}[];
	}[];
	customerBusinessDocuments: CustomerBusinessDocumentPayload<'json'>[];
	customerLegalDocuments: CustomerLegalDocumentPayload<'json'>[];
	customerNegativeHistory: customerNegativeHistory[];
	customerRejectionHistory: CustomerRejectionHistory[];
	customerReferralDocuments: CustomerReferralDocumentPayload<'json'>[];
	debtorSlikInformation: {
		id: string;
		idebFile: null;
		name: string;
		npwp: string;
		identityNumber: null;
		plafon: null;
		outstandingDebit: null;
		principalArrears: null;
	};
	mainInformation: MainInformation;
	managementSlikInformation: ShareholderSlikInformation[];
	negativeHistoryAnalysis: {
		analysisBy: string;
		analysisResult: string;
		category: string;
	};
	rejectedHistoryAnalysis: {
		analysisBy: string;
		analysisResult: string;
		category: string;
	};
	shareholderSlikInformation: ShareholderSlikInformation[];
	slikFile: FileData;
	slikResult: string;
	isRecheckSlik?: boolean;
	slikCheckingStatus?: MasterData;
	slikCheckingDate?: string;
	slikInquiryMetric?: {
		isRobotic: boolean;
		isRoboticFailed: boolean;
		roboticStatus?: {
			failed: number;
			cancelled: number;
			notFound: number;
			requested: number;
			done: number;
			total: number;
		};
		selectedDetail?: {
			type: string;
			selected: number[];
			selectedArray: { firstIndex: number; secondIndex: number }[];
		};
	};
};

export const getProspectCreditCheck = (prospectId: string) => {
	return fetcher<ProspectCustomerCreditCheck>({
		api: '/prospect/get-customer-credit-check',
		body: { prospectId },
	});
};

export type ProspectCustomerChecking = {
	approvalHistory: {
		no: number;
		approvalBy: string;
		approvalDate: string;
		approvalNote: string;
		approvalProcess: string;
		approvalStatus: string;
	}[];
	customerBusinessDocuments: CustomerBusinessDocumentPayload<'json'>[];
	customerCompanyManagementInformation: CustomerManagementInfoPayload<'json'>[];
	customerCompanyShareholderInformation: CustomerShareholderPayload<'json'>[];
	customerLegalDocuments: CustomerLegalDocumentPayload<'json'>[];
	customerNegativeHistory: customerNegativeHistory[];
	customerRejectionHistory: {
		no: number;
		customerName: string;
		prospectId: string;
		rejectedBy: string;
		rejectedDate: string;
		rejectedId: number;
		rejectedProcess: string;
		rejectedStatus: string;
	}[];
	isDeviantCustomer: boolean;
	mainInformation: MainInformation;
	negativeMessage?: string;
	customerReferralDocuments: CustomerReferralDocumentPayload<'json'>[];
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
	isRecheckSlik?: boolean;
	slikCheck?: {
		slikFormFile: FileData;
		applicationLetterFile: FileData;
		slikCheckingStatus?: MasterData;
		slikCheckingDate?: string;
	};
	slikInquiryMetric?: {
		isRobotic: boolean;
		isRoboticFailed: boolean;
		roboticStatus?: {
			cancelled: number;
			notFound: number;
			failed: number;
			requested: number;
			done: number;
			total: number;
		};
	};
};

export const getProspectCustomerChecking = (prospectId: string) => {
	return fetcher<ProspectCustomerChecking>({
		api: '/prospect/get-customer-checking',
		body: { prospectId },
	});
};

export type CreditAnalysisResultPayload = {
	prospectId: string;
	rejectedHistoryAnalysis: string;
	negativeHistoryAnalysis: string;
	notes: string;
	dukcapilAnalysis: string;
	dttotAnalysis: string;
	dppspmAnalysis: string;
	onlineBettingAnalysis: string;
	pepAnalysis: string;
	slikFile?: FilePayload;
	slikResult?: string;
	isSlikRobotik: boolean;
	managementSlikInformation: {
		id: number;
		slikResult?: number;
		idebFile?: FilePayload | FileData;
	}[];
	shareholderSlikInformation?: {
		id: number;
		slikResult?: number;
		idebFile?: FilePayload | FileData;
	}[];
	selectedCompanyIndexes?: number[];
	selectedShareholderIndexes?: {
		referenceNumber: string;
		secondIndexes: number[];
	}[];
	answers: {
		id: number;
		answer: string;
		evidenceFile?: { id: string; name: string; path: string };
	}[];
};

export const submitCreditAnalysisResult = (
	body: CreditAnalysisResultPayload,
) => {
	return fetcher<{ message: string }>({
		api: '/prospect/submit-credit-analysis-result',
		body,
	});
};

export type CreditApprovalPayload = {
	prospectId: string;
	notes: string;
	approvalStatus: string;
	returnTo: string;
	rejectCategoryId: number;
};

export const submitCreditApproval = (body: CreditApprovalPayload) => {
	return fetcher<{ message: string }>({
		api: '/prospect/submit-credit-approval',
		body,
	});
};

export const getFRMPraSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/surveys/frm/get-pra-survey-list',
		body: defaultParams,
	});
};

export const getFRMRequestSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/surveys/frm/get-survey-list',
		body: defaultParams,
	});
};

export const getFBDHRequestSurveyList = (params?: FleetQueryParams) => {
	const defaultParams = params
		? params
		: { pagination: { pageSize: 20, pageNumber: 1 } };
	return fetcher<FleetPaginated<ProspectList>>({
		api: '/surveys/fbdh/get-survey-list',
		body: defaultParams,
	});
};

export type RequestSurveyDetail = {
	customerBusinessDocuments: CustomerBusinessDocumentPayload<'json'>[];
	customerLegalDocuments: CustomerLegalDocumentPayload<'json'>[];
	customerReferralDocuments: CustomerReferralDocumentPayload<'json'>[];
	mainInformation: MainInformation;
	prospectInformation: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: FinancingApplicationLimitInformation[];
	};
	surveyInformation: {
		id?: number;
		officeAddress: string;
		officeLat: number;
		officeLong: number;
		surveyOfficeDate: string;
		siteAddress: string;
		siteLat: number;
		siteLong: number;
		surveySiteDate: string;
		surveyParticipants: SurveyParticipant[];
	};
	approvalHistories: ApprovalHistory[];
};

export const getRequestSurveyDetail = (prospectId: string) => {
	return fetcher<RequestSurveyDetail>({
		api: '/surveys/get-survey-detail',
		body: { prospectId },
	});
};

export type RequestSurveyPayload = {
	prospectId: string;
	officeAddress: string;
	officeLat: number;
	officeLong: number;
	surveyOfficeDate: string;
	siteAddress: string;
	siteLat: number;
	siteLong: number;
	surveySiteDate: string;
	participants: SurveyParticipant[];
	notes: string;
};

export const submitFRMRequestSurvey = (body: RequestSurveyPayload) => {
	return fetcher({ api: '/surveys/frm/request-survey', body });
};

export type ProspectPlafonList = {
	items: {
		no: number;
		prospectPlafonId: number;
		prospectId: string;
		existingFinancingLimit: string;
		financingLimitType: string;
		outstandingPrincipal: string;
		financingPurpose: string;
		limitApplication: string;
		totalLimitApplication: string;
		segmentMarket: string;
	}[];
};

export const getProspectPlafonList = (prospectId: string) => {
	return fetcher<ProspectPlafonList>({
		api: '/prospect-plafons/get-list',
		body: { prospectId },
	});
};

export type FinancingTypes = { parent: MasterData; items: MasterData[] };

export type ProspectInformation = {
	prospectPlafonInformation: {
		prospectId: string;
		orderSource: string;
		customerId: string;
		customerName: string;
		prospectStatus: string;
		customerGroup: { id: number; value: string };
		industrySector: {
			id: number;
			sectorLevel5: string;
			sectorLevel4: string;
			sectorLevel3: string;
			sectorLevel2: string;
			sectorLevel1: string;
			industryClass: string;
			ecosystem: string;
			portofolioGuideline: string;
		};
		slikResult?: string;
		frm: { id: string; value: string };
		creditAnalyst: { id: string; value: string };
		pendingUser?: string;
	};
	prospectPlafons: {
		existingFinancingLimitInformation: ExistingFinancingLimitInformation[];
		financingApplicationLimitInformation: {
			no: number;
			prospectPlafonId: number;
			customerId: string;
			prospectId: string;
			existingFinancingLimit: number;
			financingLimitType: string;
			outstandingPrincipal: number;
			financingPurpose: string;
			limitApplication: number;
			totalLimitApplication: number;
			segmentMarket: string;
		}[];
	};
	prospectPlafonDetails: {
		prospectId: string;
		prospectPlafonId: number;
		orderType: MasterData;
		orderSource: MasterData;
		customerName: string;
		segmentMarket: MasterData;
		referrer: {
			unitId: number;
			unitName: string;
			unitBuc?: string;
			unitGroup?: string;
			name: string;
			phoneNumber: string;
			position?: MasterData;
		};
		limitApplication: number;
		plafonType?: MasterData;
		financingTypes: FinancingTypes[];
		financingObjects: MasterData[];
	}[];
};

export const getProspectInformation = (prospectId: string) => {
	return fetcher<ProspectInformation>({
		api: '/prospect/get-information',
		body: { prospectId },
	});
};

export const getApprovalHistory = (prospectId: string) => {
	return fetcher<ApprovalHistory[]>({
		api: '/approval-history/get',
		body: { prospectId, prospectFlow: null },
	});
};

type CreditExternalQuestions = {
	answer: { id: number; value: string };
	evidenceFile: {
		id: string;
		name: string;
		format: string;
		path: unknown;
		url: string;
	};
	message: string;
	question: { id: number; value: string };
};

export type CreditExternalChecks = {
	analysisBy: string;
	analysisResult: string;
	category: string;
	creditExternalQuestions: CreditExternalQuestions[];
};

export type CustomerCheckingInformation = {
	approvalHistory: ApprovalHistory[];
	creditExternalChecks: CreditExternalChecks[];
	customerBusinessDocuments: CustomerBusinessDocument[];
	customerLegalDocuments: CustomerLegalDocument[];
	customerNegativeHistory: customerNegativeHistory[];
	customerRejectionHistory: {
		no: number;
		customerName: string;
		prospectId: string;
		rejectedBy: string;
		rejectedDate: string;
		rejectedId: number;
		rejectedProcess: string;
		rejectedStatus: string;
	}[];
	debtorSlikInformation: {
		id: string;
		idebFile: unknown | FleetFile;
		identityNumber: string;
		name: string;
		npwp: string;
		outstandingDebit: number;
		plafon: unknown;
		principalArrears: unknown;
	};
	mainInformation: MainInformation;
	managementSlikInformation: ShareholderSlikInformation[];
	negativeHistoryAnalysis: {
		analysisBy: string;
		analysisResult: string;
		category: string;
	};
	rejectedHistoryAnalysis: {
		analysisBy: string;
		analysisResult: string;
		category: string;
	};
	shareholderSlikInformation: ShareholderSlikInformation[];
	slikCheck: { slikFormFile: FleetFile; applicationLetterFile: FleetFile };
	slikInquiryMetric?: {
		isRobotic: boolean;
		isRoboticFailed: boolean;
		roboticStatus?: {
			failed: number;
			requested: number;
			done: number;
			total: number;
		};
	};
};

export const getCustomerCheckingInformation = (prospectId: string) => {
	return fetcher<CustomerCheckingInformation>({
		api: '/prospect/get-customer-checking-information',
		body: { prospectId },
	});
};

export const submitCCAReturnSLIK = (body: {
	prospectId: string;
	additionalInformation: string;
	isRecheckSlik?: boolean;
}) => {
	return fetcher({ api: '/prospect/cca/return-slik', body });
};

export const submitCCANextSLIK = (body: {
	prospectId: string;
	additionalInformation: string;
	isRecheckSlik?: boolean;
}) => {
	return fetcher({ api: '/prospect/cca/next-slik', body });
};

export enum SlikRobotikUserRoles {
	MANAGEMENT = 'CustomerCompanyManagementInformation',
	SHAREHOLDER = 'CustomerCompanyShareholderInformation',
	MANAGEMENT_SHAREHOLDER = 'CustomerCompanyManagementShareholderInformation',
}

export type SlikRobotikResultDetail = {
	prospectId: string;
	referenceNumber: string;
	referrenceId?: string;
	managementId?: string;
	cbasId: string;
	status: string;
	role: string;
	selectedIndexes?: string;
};

export type SlikRobotikResult = {
	status: string;
	details: SlikRobotikResultDetail[];
};

export const getSlikRobotikResult = (prospectId: string) => {
	return fetcher<SlikRobotikResult>({
		api: '/slik/get-result',
		body: { prospectId },
	});
};

export type RPT_SUMMARY_COMPANY = {
	appid: string;
	createdate: string;
	reffnumber: string;
	productid: string;
	namaDebitur: string;
	tanggalUpdate: string;
	ktp: string;
	alamat: string;
	ljkKet: string;
	plafonEfektifKredit: string;
	plafonEfektifLc: string;
	plafonEfektifBg: string;
	plafonEfektifSec: string;
	plafonEfektifLainnya: string;
	plafonEfektifTotal: string;
	bakiDebetKredit: string;
	bakiDebetLc: string;
	bakiDebetBg: string;
	bakiDebetSec: string;
	bakiDebetLainnya: string;
	bakiDebetTotal: string;
	krediturBankUmum: string;
	krediturBPR_S: string;
	krediturLp: string;
	krediturLainnya: string;
	kolekTerburuk: string;
	kolekBulanDataTerburuk: string;
	tanggalLahir: string;
	mmname: string;
	jenisKelaminKet: string;
	pekerjaanKet: string;
	tempatBekerja: string;
	tahunBulanKolAkhir: string;
	tahunBulan01: string;
	bidangUsahaKet: string;
	npwp: string;
};

export type RPT_SUMMARY_PERSONAL = {
	appid: string;
	createdate: string;
	reffnumber: string;
	productid: string;
	namaDebitur: string;
	tanggalUpdate: string;
	ktp: string;
	alamat: string;
	plafonEfektifKredit: string;
	plafonEfektifLc: string;
	plafonEfektifBg: string;
	plafonEfektifSec: string;
	plafonEfektifLainnya: string;
	plafonEfektifTotal: string;
	bakiDebetKredit: string;
	bakiDebetLc: string;
	bakiDebetBg: string;
	bakiDebetSec: string;
	bakiDebetLainnya: string;
	bakiDebetTotal: string;
	krediturBankUmum: string;
	krediturBPR_S: string;
	krediturLp: string;
	krediturLainnya: string;
	kolekTerburuk: string;
	kolekBulanDataTerburuk: string;
	tanggalLahir: string;
	mmname: string;
	jenisKelaminKet: string;
	pekerjaanKet: string;
	tempatBekerja: string;
	tahunBulanKolAkhir: string;
	tahunBulan01: string;
};

export type SlikRobotikDetailArray = SlikRobotikDetail[];

export type SlikRobotikDetail = {
	RPT_SUMMARY_COMPANY: RPT_SUMMARY_COMPANY[];
	RPT_SUMMARY_PERSONAL: RPT_SUMMARY_PERSONAL[];
	VW_RPT_SUMMARY_PEL: [
		{
			appid: string;
			createdate: string;
			reffnumber: string;
			namaDebitur: string;
			ljkKet: string;
			productid: string;
			identitasnew: string;
			tanggalUpdate: string;
			tanggalAwalKredit: string;
			tanggalJatuhTempo: string;
			sukuBungamin: string;
			sukuBungamax: string;
			sukuBungaavg: string;
			plafonAwal: string;
			plafon: string;
			bakiDebet: string;
			tunggakanPokok: string;
			tunggakanBunga: string;
			denda: string;
			angsuran: string;
			tenormin: string;
			tenormax: string;
			tenoravg: string;
			jenisPenggunaan2: string;
			jenisPenggunaan1: string;
			jenisPenggunaan3: string;
			maxkol: string;
			maxdpd: string;
			tahunBulanKolAkhir: string;
			jumlahHariTunggakan: string;
			tanggalRestrukturisasiAkhir: string;
		},
	];
	VW_RPT_DETAIL_FASILITAS: [
		{
			appid: string;
			createdate: string;
			reffnumber: string;
			identitasnew: string;
			KreditId: string;
			namaDebitur: string;
			productid: string;
			ljkKet: string;
			tanggalUpdate: string;
			sifatKreditKet: string;
			jenisKreditKet: string;
			Perpanjang_Kredit: string;
			noAkadAkhir: string;
			tanggalAkadAkhir: string;
			tanggalAwalKredit: string;
			tanggalMulai: string;
			tanggalJatuhTempo: string;
			tenor: string;
			jenisPenggunaanKet: string;
			sektorEkonomiKet: string;
			lokasiProyekKet: string;
			kodeValuta: string;
			sukuBunga: string;
			keterangan: string;
			kolektibilitasMax24: string;
			DPDMax24: string;
			tahunBulanKolAkhir: string;
			jumlahHariTunggakan: string;
			plafonAwal: string;
			plafon: string;
			bakiDebet: string;
			Kelonggaran: string;
			angsuranpokok: string;
			bunga: string;
			angsuranbulan: string;
			tunggakanPokok: string;
			tunggakanBunga: string;
			frekuensiTunggakan: string;
			denda: string;
			frekuensiRestrukturisasi: string;
			tanggalRestrukturisasiAkhir: string;
			kodeCaraRestrukturisasi: string;
			kondisiKet: string;
			tanggalKondisi: string;
		},
	];
	ReferenceNumber: string;
}[];

export const getSlikRobotikDetail = (prospectId: string) => {
	return fetcher<SlikRobotikDetail>({
		api: '/slik/get-detail',
		body: { prospectId },
	});
};

export type SlikRobotikHtml = { ReferenceNumber: string; html: string[] };

export const getSlikRobotikHtml = (prospectId: string) => {
	return fetcher<SlikRobotikHtml>({
		api: '/slik/get-html',
		body: { prospectId },
	});
};

export type PlafonSummaryRequest = {
	customerId: string;
	requestedFinancingLimitRequests: {
		financingLimitAmount?: number | null;
		plafonType?: string | null;
		financingLimitType?: string | null;
		expiredDate?: string | null;
	}[];
};

export type RequestedFinancingLimit = {
	mklNo: string | null;
	financingLimitAmount: number | null;
	plafonType: string | null;
	financingLimitType: string | null;
	expiredDate: string | null;
	ntfAmount: number | null;
	outstandingPrincipalAmount: number | null;
	outstandingLimitAmount: number | null;
};

export type SummaryFinancingLimitDetail = {
	financingLimitType: string | null;
	plafonType: string | null;
	status: string | null;
	financingLimitAmount: number | null;
	ntfAmount: number | null;
	outstandingPrincipalAmount: number | null;
	outstandingLimitAmount: number | null;
	obligorAmount: number | null;
};

export type SummaryGroupPlafonDetail = {
	customerId: string | null;
	customerName: string | null;
	plafonType: string | null;
	status: string | null;
	financingLimitAmount: number;
	ntfAmount: number;
	outstandingPrincipalAmount: number;
	outstandingLimitAmount: number;
	obligorAmount: number;
};

export type PlafonSummaryResponse = {
	requestedFinancingLimits: RequestedFinancingLimit[];
	summaryFinancingLimit: {
		totalNtfAmount: number | null;
		totalOutstandingPrincipalAmount: number | null;
		totalOutstandingLimitAmount: number | null;
		totalObligorAmount: number | null;
		details: SummaryFinancingLimitDetail[];
	};
	summaryGroupPlafon: {
		details: SummaryGroupPlafonDetail[];
		totalNtfAmount: number | null;
		totalOutstandingPrincipalAmount: number | null;
		totalOutstandingLimitAmount: number | null;
		totalObligorAmount: number | null;
	};
	groupBmpp: string | null;
	groupBmppId: string | null;
	totalOutstandingLimitAmount: number | null;
	totalObligorAmount: number | null;
};

export const getPlafonSummary = (body: PlafonSummaryRequest) =>
	fetcher<PlafonSummaryResponse>({
		api: '/customer-plafons/get-plafon-summary',
		body,
	});

export const cancelSlikRobotik = (prospectId: string) => {
	return fetcher({ api: '/slik/cancel', body: { prospectId } });
};

export const getIdebPdf = (refferenceNumber: string) => {
	return fetcher<{ data: string }>({
		api: '/slik/get-all-pdf',
		body: { refferenceNumber },
	});
};
